import React, { useEffect, useState } from 'react';
import { ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { mrbAPI } from '@/lib/api';

// Thumbnail strip of image attachments on an MRB case; click to enlarge.
export const MrbPhotoGallery = ({ caseId, attachments, title = 'Defect photos' }) => {
  const photos = (attachments || []).filter((a) => a.content_type?.startsWith('image/'));
  const [urls, setUrls] = useState({});
  const [open, setOpen] = useState(null);
  const key = photos.map((p) => p.id).join(',');

  useEffect(() => {
    let alive = true;
    const made = [];
    Promise.all(photos.map((p) => mrbAPI.previewAttachment(caseId, p.id)
      .then((r) => { const u = URL.createObjectURL(r.data); made.push(u); return [p.id, u]; })
      .catch(() => [p.id, null])))
      .then((pairs) => alive && setUrls(Object.fromEntries(pairs)));
    return () => { alive = false; made.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, key]);

  if (!photos.length) return null;
  return (
    <div data-testid="mrb-photo-gallery">
      <p className="text-[11px] font-medium text-muted-foreground mb-1">{title} ({photos.length})</p>
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <button key={p.id} type="button" onClick={() => urls[p.id] && setOpen(p)}
            className="w-20 h-20 rounded border overflow-hidden bg-slate-50 flex items-center justify-center hover:ring-2 hover:ring-amber-400 transition-shadow"
            data-testid={`mrb-photo-thumb-${i}`}>
            {urls[p.id] ? <img src={urls[p.id]} alt={p.filename} className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-300" />}
          </button>
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl" data-testid="mrb-photo-preview-dialog">
          <DialogHeader><DialogTitle className="text-sm">{open?.filename}</DialogTitle></DialogHeader>
          {open && <img src={urls[open.id]} alt={open.filename} className="w-full max-h-[70vh] object-contain rounded" />}
          {open && <p className="text-xs text-muted-foreground">{open.description} • {open.uploaded_by_name}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Loads the linked MRB case and shows its quarantine photos (used on Internal NCR page).
export const MrbCasePhotos = ({ caseId }) => {
  const [attachments, setAttachments] = useState([]);
  useEffect(() => {
    if (!caseId) return;
    mrbAPI.getCase(caseId).then((r) => setAttachments(r.data.attachments || [])).catch(() => setAttachments([]));
  }, [caseId]);
  if (!caseId || !attachments.some((a) => a.content_type?.startsWith('image/'))) return null;
  return (
    <div className="rounded border bg-amber-50/40 p-3" data-testid="ncr-quarantine-photos">
      <MrbPhotoGallery caseId={caseId} attachments={attachments} title="Quarantine photos" />
      <p className="text-[10px] text-muted-foreground mt-1">From the MRB case. Included in the 8D Word report after problem photos (max 5 in total).</p>
    </div>
  );
};
